var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useQuery } from '@tanstack/react-query';
import { GET_CREATORS, SEARCH_TWITCH_USERS } from 'root/src/shared/descriptions/endpoints/endpointIds';
import { searchPlatformCreatorsKey } from 'root/src/shared/constants/keys';
import { Platform } from 'root/src/shared/@types/platforms';
import { redesignApiFetch } from 'root/src/client/v2/_pages/add-campaign/redesign/lib/redesignDareDropApi';
import { getYoutubeChannels } from 'root/src/client/util/youtubeClientApi';
const SEARCH_LIMIT = 10;
const searchTwitchCreators = (query) => __awaiter(void 0, void 0, void 0, function* () {
    const { data } = yield redesignApiFetch(SEARCH_TWITCH_USERS, {
        input: query,
        after: null,
        first: SEARCH_LIMIT,
    });
    return (data !== null && data !== void 0 ? data : []).map(channel => ({
        platform: Platform.Twitch,
        platformId: String(channel.id),
        displayName: channel.display_name,
        image: channel.thumbnail_url,
    }));
});
// Straight to the YouTube Data API from the browser, the same call the legacy assignee picker makes.
// We have no server-side channel search to route this through, and the util already swallows its own
// errors and returns an empty list, so a quota/network failure degrades to "no results".
const searchYoutubeCreators = (query) => __awaiter(void 0, void 0, void 0, function* () {
    const channels = yield getYoutubeChannels(query);
    return channels.map(channel => ({
        platform: Platform.YouTube,
        platformId: channel.id,
        displayName: channel.label,
        image: channel.image,
    }));
});
// TikTok has no usable public search API, so this searches our own creators table (trigram similarity
// on displayName). Only creators DareDrop already knows are findable, which is what the legacy picker
// offered too.
const searchTikTokCreators = (query) => __awaiter(void 0, void 0, void 0, function* () {
    const creators = yield redesignApiFetch(GET_CREATORS, {
        value: { query, platform: Platform.TikTok },
        section: searchPlatformCreatorsKey,
        pageItemLength: SEARCH_LIMIT,
    });
    return (creators !== null && creators !== void 0 ? creators : []).map(creator => ({
        platform: Platform.TikTok,
        platformId: creator.platformId,
        displayName: creator.displayName,
        image: creator.image,
    }));
});
const SEARCH_BY_PLATFORM = {
    [Platform.Twitch]: searchTwitchCreators,
    [Platform.YouTube]: searchYoutubeCreators,
    [Platform.TikTok]: searchTikTokCreators,
};
// Creator typeahead for the brand dare picker, one source per platform. Results are cached per
// (platform, query) so flipping the platform pills back and forth does not re-hit the APIs.
export const useCreatorSearch = (platform, query, options) => {
    var _a;
    return useQuery({
        queryKey: ['redesign', 'creatorSearch', platform, query],
        queryFn: () => __awaiter(void 0, void 0, void 0, function* () { return SEARCH_BY_PLATFORM[platform](query); }),
        enabled: (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true,
        staleTime: 60000,
        retry: false,
    });
};
